import { IonIcon } from "@ionic/react";
import { useRef, useState } from "react";
import { type FieldApi } from "@tanstack/react-form";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import {
  type PDOKAddress,
  lookupAddress,
  parsePDOKAddress,
  suggestAddresses,
} from "@/lib/services/pdok";
import { cn } from "@/lib/style/cva.config";
import { logger } from "@/logger";

export type AddressAutocompleteProps = {
  label: string;
  description?: string;
  required?: boolean;
  onAddressSelect?: (address: {
    addressLine: string;
    zipCode: string;
    city: string;
    state: string;
  }) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

/**
 * Address autocomplete field component using PDOK API for Ionic app
 *
 * Features:
 *
 * - Debounced search (300ms)
 * - Minimum 2 characters before searching
 * - Fetches full address details on selection using lookup endpoint
 * - Shows loading state
 * - Ionic-styled UI
 *
 * @example
 *   ```tsx
 *   <form.Field
 *     name="addressLine"
 *     children={(field: any) => (
 *       <AddressAutocomplete
 *         field={field}
 *         label="Adres"
 *         onAddressSelect={(address) => {
 *           form.store.setState((prev) => ({
 *             ...prev,
 *             values: {
 *               ...prev.values,
 *               addressLine: address.addressLine,
 *               zipCode: address.zipCode,
 *               city: address.city,
 *               state: address.state,
 *             },
 *           }));
 *         }}
 *       />
 *     )}
 *   />
 *   ```;
 */
export function AddressAutocomplete({
  label,
  description,
  required = false,
  field,
  onAddressSelect,
}: AddressAutocompleteProps) {
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  const [suggestions, setSuggestions] = useState<PDOKAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = async (value: string) => {
    field.handleChange(value);

    // Debounce the API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimeout.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await suggestAddresses(value, 10);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        logger.error("Failed to fetch address suggestions", { error });
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const handleSuggestionClick = async (suggestion: PDOKAddress) => {
    logger.info("Address suggestion clicked", { suggestion });

    // Hide suggestions immediately for better UX
    setShowSuggestions(false);
    setSuggestions([]);

    // Set loading state
    setIsLoading(true);

    try {
      // Lookup full address details using the ID
      const fullAddress = await lookupAddress(suggestion.id);

      if (fullAddress) {
        logger.info("Full address details fetched", { fullAddress });

        // Parse the full address
        const parsed = parsePDOKAddress(fullAddress);
        logger.info("Parsed address", { parsed });

        // Update the field value
        field.handleChange(parsed.addressLine);

        // Call the callback to update other fields
        if (onAddressSelect) {
          onAddressSelect({
            addressLine: parsed.addressLine,
            zipCode: parsed.zipCode,
            city: parsed.city,
            state: parsed.state,
          });
        }
      } else {
        logger.warn("Could not fetch full address details");
      }
    } catch (error) {
      logger.error("Failed to lookup address", { error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col gap-2">
      {/* Label */}
      <div className="flex items-center gap-1.5">
        <label htmlFor={field.name} className="text-md font-semibold text-txt-primary">
          {label}
          {required && <span className="text-error-120"> *</span>}
        </label>
        {description && (
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-6 w-6 items-center justify-center"
          >
            <IonIcon icon={informationCircleOutline} className="text-xl text-txt-tertiary" />
          </button>
        )}
      </div>

      {/* Input */}
      <input
        id={field.name}
        name={field.name}
        type="text"
        value={field.state.value ?? ""}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={() => {
          // Delay hiding suggestions to allow click events to fire
          setTimeout(() => {
            setShowSuggestions(false);
            field.handleBlur();
          }, 200);
        }}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        disabled={field.state.meta.isValidating || field.form.state.isSubmitting}
        placeholder=""
        className={cn(
          "w-full rounded-lg border border-bordercolor-primary bg-white px-4 py-[13px] text-17 font-medium leading-tight text-txt-primary",
          "placeholder:text-txt-tertiary",
          "focus:border-brand-primary focus:ring-brand-primary focus:outline-none focus:ring-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-error-100 focus:border-error-100 focus:ring-error-100",
        )}
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-bordercolor-primary bg-white shadow-lg">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-txt-tertiary">Laden...</div>
          ) : (
            suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-3 text-left text-sm font-medium text-txt-secondary hover:bg-neutral-50 active:bg-neutral-100"
              >
                {suggestion.weergavenaam}
              </button>
            ))
          )}
        </div>
      )}

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}

      {/* Info Modal */}
      {description && (
        <ModalSheetAutoHeight ref={infoModalRef}>
          <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
            {/* Header with title and close */}
            <div className="flex shrink-0 items-center justify-between">
              <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{label}</h2>
              <button
                type="button"
                onClick={() => infoModalRef.current?.dismiss()}
                className="flex size-12 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
              </button>
            </div>

            {/* Content */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <p className="whitespace-pre-line text-md font-medium leading-[1.5] text-txt-tertiary">
                {description}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => infoModalRef.current?.dismiss()}
              className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-md font-semibold text-txt-inverted"
            >
              Begrepen
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}
    </div>
  );
}
